import React from 'react';
import {
  BarChart3, TrendingUp, Target, Eye, PieChart, LineChart,
  Zap, Database, Shield, AlertCircle,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const PerformanceAnalyticsPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'Performance Analytics',
      title: (
        <>
          Advanced performance analytics <em>for renewable energy</em>
        </>
      ),
      lede: 'Transform raw data into actionable insights with our comprehensive performance analytics platform. Track, analyze, and optimize every aspect of your renewable energy operations.',
      image: '/assets/img/general/ai-generated-8685407_1280.jpg',
      imageAlt: 'Renewable energy analytics',
      actions: [
        { label: 'Request a walkthrough', to: '/contact' },
        { label: 'Explore all services', to: '/service' },
      ],
      metrics: [
        { value: '25%', label: 'Efficiency increase' },
        { value: 'Real-time', label: 'Data analytics' },
        { value: '500+', label: 'Data points' },
        { value: '15+', label: 'KPI metrics' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'A complete analytics solution',
      lede: 'Our performance analytics platform provides everything you need to understand, optimize, and improve your renewable energy operations.',
      body: [
        'Production, efficiency, losses and financial return are analysed together rather than in isolation, so every reported number can be traced back to the asset and the interval that produced it.',
      ],
      checklist: [
        'Real-time performance analytics and reporting',
        'Historical data analysis and trend identification',
        'Customizable dashboards and visualizations',
        'Automated performance reports and alerts',
        'Comparative analysis across multiple assets',
        'Energy production forecasting and predictions',
        'ROI and financial performance metrics',
        'Integration with existing data systems',
      ],
      image: '/assets/img/general/photovoltaic-2814504_1280.jpg',
      imageAlt: 'Photovoltaic array',
      imageCaption: 'Transform data into actionable insights with advanced analytics and visualization',
    }}
    capabilities={{
      eyebrow: 'Analytics features',
      title: 'Powerful analytics capabilities',
      lede: 'Comprehensive analytics tools designed to maximize renewable energy performance.',
      items: [
        {
          icon: BarChart3,
          title: 'Advanced analytics',
          description: 'Comprehensive data analysis with powerful visualization tools for deep performance insights.',
        },
        {
          icon: TrendingUp,
          title: 'Trend analysis',
          description: 'Identify performance trends and patterns to optimize energy generation and efficiency.',
        },
        {
          icon: Target,
          title: 'KPI tracking',
          description: 'Monitor and track key performance indicators across all your renewable energy assets.',
        },
        {
          icon: Eye,
          title: 'Performance benchmarking',
          description: 'Compare asset performance against industry standards and historical benchmarks.',
        },
      ],
    }}
    coverage={{
      eyebrow: 'Analytics capabilities',
      title: 'Comprehensive performance metrics',
      lede: 'Analyze every aspect of your renewable energy performance.',
      items: [
        {
          icon: LineChart,
          title: 'Energy production analysis',
          description: 'Track and analyze energy production patterns across all assets and timeframes.',
        },
        {
          icon: PieChart,
          title: 'Efficiency metrics',
          description: 'Monitor performance ratios, capacity factors, and overall efficiency indicators.',
        },
        {
          icon: AlertCircle,
          title: 'Loss analysis',
          description: 'Identify and quantify energy losses to pinpoint optimization opportunities.',
        },
        {
          icon: BarChart3,
          title: 'Financial analytics',
          description: 'Analyze revenue, costs, and ROI with comprehensive financial reporting.',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Business value',
      title: 'Drive measurable results',
      lede: 'Achieve significant improvements in performance and profitability.',
      items: [
        {
          icon: Zap,
          title: 'Increase efficiency',
          description: 'Boost operational efficiency by 15-25% through data-driven performance optimization.',
        },
        {
          icon: Database,
          title: 'Data-driven decisions',
          description: 'Make informed decisions based on comprehensive analytics and real-time insights.',
        },
        {
          icon: TrendingUp,
          title: 'Revenue growth',
          description: 'Maximize revenue through optimized performance and reduced operational losses.',
        },
        {
          icon: Shield,
          title: 'Risk management',
          description: 'Identify and mitigate performance risks before they impact your operations.',
        },
      ],
    }}
    cta={{
      title: 'Ready to optimize performance?',
      body: 'Start leveraging advanced analytics to maximize your renewable energy operations.',
      primary: { label: 'Talk to our team', to: '/contact' },
      secondary: { label: 'View all services', to: '/service' },
    }}
  />
);

export default PerformanceAnalyticsPage;
